// Swipe a recipe right to put it on (or take it off) the grocery list. Vertical scrolling is left
// to the browser (touch-action: pan-y); a swipe that moved the card cancels the tap that follows.
import type { ComponentChildren } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { Bag } from '../icons';
import { inGrocery, toggleGrocery } from '../grocery-selection';
import { useT } from '../i18n';

const TRIGGER = 72; // px of horizontal travel that counts as a swipe
const MAX = 120;

export function SwipeToGrocery({ id, onToast, children }: { id: number; onToast: (m: string) => void; children: ComponentChildren }) {
  const t = useT();
  const [dx, setDx] = useState(0);
  const [on, setOn] = useState(() => inGrocery(id));
  const g = useRef<{ x: number; y: number; dir: '' | 'x' | 'y' } | null>(null);
  const moved = useRef(false);

  const down = (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    g.current = { x: e.clientX, y: e.clientY, dir: '' };
    moved.current = false;
    setOn(inGrocery(id));
  };
  const move = (e: PointerEvent) => {
    const s = g.current;
    if (!s) return;
    const ddx = e.clientX - s.x, ddy = e.clientY - s.y;
    if (!s.dir && Math.hypot(ddx, ddy) > 10) s.dir = ddx > 0 && Math.abs(ddx) > Math.abs(ddy) * 1.5 ? 'x' : 'y';
    if (s.dir !== 'x') return;
    if (!moved.current) (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    moved.current = true;
    setDx(Math.max(0, Math.min(MAX, ddx)));
  };
  const up = () => {
    if (g.current?.dir === 'x' && dx >= TRIGGER) {
      const next = toggleGrocery(id);
      setOn(next);
      onToast(next ? t('recipe.groceryAdded') : t('recipe.groceryRemoved'));
      navigator.vibrate?.(15);
    }
    g.current = null;
    setDx(0);
  };

  return (
    <div class={'swipe' + (dx ? ' dragging' : '')} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      onClickCapture={(e) => { if (moved.current) { e.preventDefault(); e.stopPropagation(); moved.current = false; } }}>
      <div class={'swipe-under' + (dx >= TRIGGER ? ' ready' : '')} aria-hidden="true">
        <Bag size={20} />{on ? t('swipe.remove') : t('swipe.add')}
      </div>
      <div class="swipe-over" style={dx ? `transform:translateX(${dx}px)` : ''}>{children}</div>
    </div>
  );
}
